import React, { useState, useEffect } from 'react';
import { ShoppingCart, Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { getCartCount } = useCart();
  const cartCount = getCartCount();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const styles = {
    header: {
      position: 'sticky',
      top: 0,
      zIndex: 1000,
      background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
      borderBottom: '2px solid #c59b00',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
    },
    container: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: isMobile ? '1rem' : '1rem 2rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    logoContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '0.4rem' : '1rem',
      cursor: 'pointer',
    },
    logoIcon: {
      background: 'linear-gradient(135deg, #c59b00 0%, #ffd866 100%)',
      padding: isMobile ? '0.4rem' : '0.8rem',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 4px 15px rgba(197, 155, 0, 0.25)',
      transition: 'transform 0.3s ease',
    },
    logo: {
      fontSize: isMobile ? '1.1rem' : '1.4rem',
      fontWeight: '800',
      color: '#c59b00',
      letterSpacing: '1px',
      fontFamily: "'Poppins', 'Montserrat', sans-serif",
      transition: 'transform 0.3s ease',
    },
    nav: {
      display: isMobile ? 'none' : 'flex',
      gap: '3rem',
      alignItems: 'center',
    },
    navLink: {
      color: '#ffffff',
      textDecoration: 'none',
      fontSize: '1.1rem',
      fontWeight: '500',
      position: 'relative',
      padding: '0.5rem 0',
      transition: 'color 0.3s ease',
      cursor: 'pointer',
      fontFamily: "'Poppins', sans-serif",
    },
    cartButton: {
      background: 'linear-gradient(135deg, #c59b00 0%, #ffd866 100%)',
      border: 'none',
      borderRadius: '50px',
      padding: isMobile ? '0.4rem 0.8rem' : '0.8rem 1.5rem',
      color: '#000000',
      fontWeight: '700',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.4rem',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(197, 155, 0, 0.25)',
      fontSize: isMobile ? '0.8rem' : '1rem',
      textDecoration: 'none',
    },
    cartBadge: {
      background: '#000000',
      color: '#c59b00',
      borderRadius: '50%',
      width: isMobile ? '16px' : '20px',
      height: isMobile ? '16px' : '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: isMobile ? '0.65rem' : '0.75rem',
      fontWeight: 'bold',
    },
    rightSection: {
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '0.5rem' : '1rem',
    },
    mobileMenuButton: {
      display: isMobile ? 'block' : 'none',
      background: 'transparent',
      border: 'none',
      color: '#c59b00',
      cursor: 'pointer',
      padding: '0.5rem',
    },
    mobileNav: {
      display: isMenuOpen ? 'flex' : 'none',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.98)',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '2rem',
      zIndex: 999,
      padding: '2rem',
    },
    mobileNavLink: {
      color: '#ffffff',
      textDecoration: 'none',
      fontSize: '1.5rem',
      fontWeight: '600',
      padding: '1rem',
      width: '100%',
      textAlign: 'center',
      transition: 'all 0.3s ease',
      borderRadius: '12px',
    },
    closeButton: {
      position: 'absolute',
      top: '1.5rem',
      right: '1.5rem',
      background: 'transparent',
      border: 'none',
      color: '#c59b00',
      cursor: 'pointer',
    },
  };

  const LinkWithHover = ({ children, href, mobile }) => {
    const [isHovered, setIsHovered] = useState(false);

    if (mobile) {
      return (
        <Link
          to={href}
          style={{
            ...styles.mobileNavLink,
            background: isHovered ? 'rgba(197, 155, 0, 0.12)' : 'transparent',
            color: isHovered ? '#c59b00' : '#ffffff',
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={() => setIsMenuOpen(false)}
        >
          {children}
        </Link>
      );
    }

    return (
      <Link
        to={href}
        style={{
          ...styles.navLink,
          color: isHovered ? '#c59b00' : '#ffffff',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {children}
        <span
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: isHovered ? '100%' : '0%',
            height: '3px',
            background: 'linear-gradient(90deg, #c59b00, #ffd866)',
            transition: 'width 0.3s ease',
          }}
        />
      </Link>
    );
  };

  const CartButtonWithHover = () => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <Link
        to="/panier"
        style={{
          ...styles.cartButton,
          transform: isHovered ? 'translateY(-2px) scale(1.05)' : 'translateY(0) scale(1)',
          boxShadow: isHovered
            ? '0 6px 25px rgba(197,155,0,0.45)'
            : '0 4px 15px rgba(197,155,0,0.25)',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <ShoppingCart size={isMobile ? 18 : 20} />
        {!isMobile && 'Panier'}
        {cartCount > 0 && <span style={styles.cartBadge}>{cartCount}</span>}
      </Link>
    );
  };

  const LogoWithHover = () => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <Link
        to="/accueil"
        style={{ textDecoration: 'none', color: 'inherit' }}
        onClick={() => setIsMenuOpen(false)}
      >
        <div
          style={styles.logoContainer}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <img
            src="/logo.jpg"
            alt="Destockmax logo"
            style={{
              width: isMobile ? '36px' : '52px',
              height: isMobile ? '36px' : '52px',
              objectFit: 'contain',
              borderRadius: '8px',
              boxShadow: isHovered ? '0 6px 20px rgba(197,155,0,0.25)' : 'none',
            }}
          />
          <div
            style={{
              ...styles.logo,
              fontSize: isMobile ? '1rem' : '1.2rem',
              marginLeft: '0.6rem',
              transform: isHovered ? 'scale(1.03)' : 'scale(1)',
            }}
          >
            DESTOCKMAX
          </div>
        </div>
      </Link>
    );
  };

  return (
    <header style={styles.header}>
      <div style={styles.container}>
        <LogoWithHover />

        <nav style={styles.nav}>
          <LinkWithHover href="/accueil">Accueil</LinkWithHover>
          <LinkWithHover href="/catalogue">Catalogues</LinkWithHover>
          <LinkWithHover href="/apropos">À propos</LinkWithHover>
          <LinkWithHover href="/contact">Contact</LinkWithHover>
          <CartButtonWithHover />
        </nav>

        <div style={styles.rightSection}>
          {isMobile && <CartButtonWithHover />}
          <button
            style={styles.mobileMenuButton}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <Menu size={28} />
          </button>
        </div>
      </div>

      {isMenuOpen && (
        <div style={styles.mobileNav}>
          <button
            style={styles.closeButton}
            onClick={() => setIsMenuOpen(false)}
          >
            <X size={32} />
          </button>
          <LinkWithHover href="/accueil" mobile>Accueil</LinkWithHover>
          <LinkWithHover href="/catalogue" mobile>Catalogues</LinkWithHover>
          <LinkWithHover href="/apropos" mobile>À propos</LinkWithHover>
          <LinkWithHover href="/contact" mobile>Contact</LinkWithHover>
        </div>
      )}
    </header>
  );
};

export default Header;